import { useEffect, useLayoutEffect, useState } from "react"
import { useAuthContext } from "@contexts/authContext"
import { useNavigate } from 'react-router-dom'
import useLoading from "@hooks/useLoading"
import styled from '@emotion/styled'
import { size } from "@styles/theme"
import { ButtonBase, Popover, IconButton, LinearProgress } from "@mui/material"
import StatusContentSelect from "./StatusContentSelect"
import ModalContainer from "@elements/ModalContainer"
import StatusContentMeta from "./StatusContentMeta"
import { TbInfoCircleFilled } from "react-icons/tb"
import { MapView } from "@contexts/mapDataContext"
import { PiCaretLeftBold } from "react-icons/pi"
import { Toast } from "@base-ui/react"

const Header = styled.div`
  width: 100%;
  height: ${size(8)};
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${size(0.75)};
  z-index: 1;
  background-color: black;
`

const HeaderText = styled.h3`
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
`
  
const HeaderClearfix = styled.div`
  width: 100%;
  height: ${size(8)};
  flex-shrink: 0;
`

const Contents = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
`

const ContentsMain = styled.div`
  flex-grow: 1;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
`

const NearbyStatusDialog = ({ isOpen, setClosed, tagline }) => {
  const { isLoading, isLoadingDelayed, setIsLoading } = useLoading()
  const { state: authState, handleNearbyUpdate, } = useAuthContext()
  const toastManager = Toast.useToastManager()
  const navigate = useNavigate()
  const [currentPage, setCurrentPage] = useState(0)
  const [newStatus, setNewStatus] = useState(null)
  const [prevIsOpen, setPrevIsOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [metaState, setMetaState] = useState({
    tagline: tagline || authState?.user?.extendedInfos?.tagline,
    position: authState?.user?.tempStatusMeta?.position || '',
    hosting: authState?.user?.tempStatusMeta?.hosting || '',
  })

  // Resets the state as needed when dialog opens and closes
  useLayoutEffect(() => {
    if (isOpen && !prevIsOpen) {
      setCurrentPage(0)
      setNewStatus(null)
      setDefaultMetaState()
      setPrevIsOpen(isOpen)
    } else if (!isOpen && prevIsOpen) {
      setPrevIsOpen(isOpen)
    }
  }, [isOpen])

  // Sets default state when changes to the user are made
  useEffect(() => {
    setDefaultMetaState()
  }, [authState?.user])

  const setDefaultMetaState = () => setMetaState({
    tagline: tagline || authState?.user?.extendedInfos?.tagline,
    position: authState?.user?.tempStatusMeta?.position || '',
    hosting: authState?.user?.tempStatusMeta?.hosting || '',
  })

  const handleMetaStateChange = (e) => {
    setMetaState({ ...metaState, [e.target.name]: e.target.value })
  }

  const generateUpdatedUser = (data) => ({
    ...authState?.user,
    tempStatus: data.temp_status,
    tempStatusMeta: data.temp_status_meta,
    extendedInfos: data.extended_infos_attributes,
  })

  const submitTempStatus = () => {
    setIsLoading(true)
    const data = {}
    data.temp_status = newStatus
    data.temp_status_meta = { position: metaState.position, hosting: metaState.hosting }
    data.extended_infos_attributes = { tagline: metaState.tagline }
    MapView.updateUser(authState?.user?.nearbyUuid, generateUpdatedUser(data))
    handleNearbyUpdate(data)
      .then(() => {
        setClosed()
        setIsLoading(false)
        toastManager.add({
          text: 'looking status updated',
        });
      })
      .catch(() => {
        setIsLoading(false)
        toastManager.add({
          text: 'failed to update looking status',
        });
      })
  }

  const handleInfoClick = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleInfoClose = () => {
    setAnchorEl(null);
  };

  const backOrExit = () => {
    if (currentPage === 0) {
      navigate(-1)
    } else {
      setNewStatus(null)
      setCurrentPage(0)
    }
  }

  const handleNext = () => {
    if (!newStatus) {
      setNewStatus(authState?.user?.tempStatus)
    }
    setCurrentPage(1)
  }

  return(
    <ModalContainer
      noHeader
      open={isOpen}
      onClose={setClosed}>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleInfoClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}>
        <div style={{ padding: size(2), width: size(30), maxWidth: '100vw', backgroundColor: 'rgb(80,80,80)' }}>
          The <em>Status Feature</em> allows you to tell nearby people what you're looking for <em>at this moment</em>.
          <br /><br />For example, if you see a purple profile with the "eyes" icon, that user is looking to be intimate <em>right now</em>.
        </div>
      </Popover>
      <LinearProgress style={{ position: 'absolute', visibility: isLoadingDelayed ? 'visible' : 'hidden', top: 0, left: 0, width: '100%', zIndex: 2 }} />
      <Header>
        <HeaderText>
          <ButtonBase
            onClick={backOrExit}
            style={{ borderRadius: '100px' }}>
            <PiCaretLeftBold size={size(3)} style={{ marginRight: size(1) }} />
          </ButtonBase>
          Looking Status
        </HeaderText>
        <IconButton onClick={handleInfoClick}>
          <TbInfoCircleFilled size={size(3)} color='grey' />
        </IconButton>
      </Header>
      <Contents>
        <HeaderClearfix />
        <ContentsMain>
          {currentPage === 0
            ? <StatusContentSelect afterSelect={(val) => {
                setNewStatus(val)
                setCurrentPage(1)
              }}/>
            : <StatusContentMeta
                handleChange={handleMetaStateChange}
                metaState={metaState}
                newStatus={newStatus}
                isLoading={isLoading}
                submitTempStatus={submitTempStatus}
              />
          }
        </ContentsMain>
      </Contents>
    </ModalContainer>
  )
}

export default NearbyStatusDialog
